import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  ArrowRight, 
  CheckCircle, 
  AlertCircle,
  ExternalLink,
  Copy,
  RefreshCw,
  Shield,
  Building
} from 'lucide-react'
import { generateOAuthUrl, handleOAuthCallback, discoverAllAccounts } from '@/utils/gmbWorkerApi'

type SetupStep = 'start' | 'oauth' | 'callback' | 'discover' | 'complete'

export default function GMBSetup() {
  const [currentStep, setCurrentStep] = useState<SetupStep>('start')
  const [oauthUrl, setOauthUrl] = useState('')
  const [authCode, setAuthCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [discoveryResults, setDiscoveryResults] = useState<any>(null)

  // Check if we have a code in the URL (OAuth callback)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    if (code) {
      setAuthCode(code)
      setCurrentStep('callback')
      handleCallback(code)
    }
  }, [])

  const startOAuthFlow = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await generateOAuthUrl()
      if (result.success && result.data) {
        setOauthUrl(result.data.auth_url)
        setCurrentStep('oauth')
      } else {
        setError(result.error || 'Failed to generate OAuth URL')
      }
    } catch (err) {
      setError('Failed to start OAuth flow')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCallback = async (code: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await handleOAuthCallback(code)
      if (result.success) {
        setCurrentStep('discover')
        // Automatically start discovery
        await startDiscovery()
      } else {
        setError(result.error || 'Failed to complete OAuth')
      }
    } catch (err) {
      setError('Failed to handle OAuth callback')
    } finally {
      setIsLoading(false)
    }
  }

  const startDiscovery = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await discoverAllAccounts()
      if (result.success && result.data) {
        setDiscoveryResults(result.data)
        setCurrentStep('complete')
      } else {
        setError(result.error || 'Failed to discover accounts')
      }
    } catch (err) {
      setError('Failed to discover GMB accounts')
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // You could add a toast notification here
  }

  const renderStep = () => {
    switch (currentStep) {
      case 'start':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Connect Google My Business</CardTitle>
              <CardDescription>
                Set up your Google Business Profile integration in a few simple steps
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-primary/10 p-2">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Secure Authentication</h4>
                    <p className="text-sm text-muted-foreground">
                      We'll use Google's official OAuth2 flow to securely connect your account
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-primary/10 p-2">
                    <Building className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Multi-Location Support</h4>
                    <p className="text-sm text-muted-foreground">
                      Manage all your franchise locations from one dashboard
                    </p>
                  </div>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Make sure you're signed in to the Google account that manages your business listings
                </AlertDescription>
              </Alert>

              <Button onClick={startOAuthFlow} disabled={isLoading} className="w-full">
                {isLoading ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2 h-4 w-4" />
                )}
                Start Setup
              </Button>
            </CardContent>
          </Card>
        )

      case 'oauth':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Authorize Access</CardTitle>
              <CardDescription>
                Click the button below to authorize FixMyBrick to access your Google Business Profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Authorization URL</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(oauthUrl)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground break-all">
                    {oauthUrl}
                  </p>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You'll be redirected to Google to grant permissions. Make sure to select all requested permissions.
                </AlertDescription>
              </Alert>

              <div className="flex gap-3">
                <Button 
                  onClick={() => window.open(oauthUrl, '_blank')}
                  className="flex-1"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Authorize with Google
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep('start')}
                >
                  Back
                </Button>
              </div>
            </CardContent>
          </Card>
        )

      case 'callback':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Processing Authorization</CardTitle>
              <CardDescription>
                We're completing the setup with Google...
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Please wait while we process your authorization...</p>
            </CardContent>
          </Card>
        )

      case 'discover':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Discovering Your Locations</CardTitle>
              <CardDescription>
                We're finding all your Google Business Profile locations...
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">This may take a few moments...</p>
            </CardContent>
          </Card>
        )

      case 'complete':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Setup Complete!</CardTitle>
              <CardDescription>
                Your Google Business Profile is now connected
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-center py-4">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>

              {discoveryResults && (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">{discoveryResults.total_accounts}</div>
                      <p className="text-sm text-muted-foreground">Business Accounts</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">{discoveryResults.total_locations}</div>
                      <p className="text-sm text-muted-foreground">Locations Found</p>
                    </div>
                  </div>

                  {discoveryResults.locations && discoveryResults.locations.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Discovered Locations:</h4>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {discoveryResults.locations.map((location: any) => (
                          <div key={location.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                            <div>
                              <p className="text-sm font-medium">{location.name}</p>
                              <p className="text-xs text-muted-foreground">{location.address}</p>
                            </div>
                            {location.place_id && (
                              <Badge variant="secondary" className="text-xs">Verified</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  onClick={() => window.location.href = '/integrations/gmb'}
                  className="flex-1"
                >
                  Go to GMB Dashboard
                </Button>
                <Button
                  variant="outline"
                  onClick={startDiscovery}
                  disabled={isLoading}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        )
    }
  }

  return (
    <div className="container max-w-2xl mx-auto py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">GMB Setup</h1>
        <p className="text-muted-foreground">
          Connect your Google Business Profile to manage all locations
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {[
          { step: 'start', label: 'Start' },
          { step: 'oauth', label: 'Authorize' },
          { step: 'discover', label: 'Discover' },
          { step: 'complete', label: 'Complete' }
        ].map((item, index) => {
          const isActive = item.step === currentStep
          const isComplete = ['start', 'oauth', 'discover', 'complete'].indexOf(currentStep) > 
                           ['start', 'oauth', 'discover', 'complete'].indexOf(item.step)
          
          return (
            <React.Fragment key={item.step}>
              <div className="flex flex-col items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                  ${isComplete ? 'bg-primary text-primary-foreground' : 
                    isActive ? 'bg-primary text-primary-foreground' : 
                    'bg-muted text-muted-foreground'}
                `}>
                  {isComplete ? <CheckCircle className="h-5 w-5" /> : index + 1}
                </div>
                <span className="text-xs mt-1">{item.label}</span>
              </div>
              {index < 3 && (
                <div className={`flex-1 h-1 mx-2 ${
                  isComplete ? 'bg-primary' : 'bg-muted'
                }`} />
              )}
            </React.Fragment>
          )
        })}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {renderStep()}
    </div>
  )
}